import React, { useState, useEffect } from 'react';
import { replace, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Canvas from './components/Canvas';
import ExpertBoard from './components/pages/ExpertBoard';
import SetMetting from './components/SetMetting';
import AiAnalysisView from './components/AiAnalysisView';
import VideoCallPage from './components/pages/VideoCallPage';
import WhiteboardPage from './components/pages/WhiteboardPage';
import ConversationPage from './components/pages/ConversationPage';
import ContactUs from './components/pages/ContactUs';
import Settings from './components/pages/Settings';
import styled from 'styled-components';
import ProtectedRoute from './auth/protected-route';
import FormFillingProtectedRoute from './auth/form-filling-protected-route';
import FormFillingPage from './components/pages/FormFillingPage';
import PaymentPage from './components/pages/PaymentPage';
import ServiceQuestionnairePage from './components/pages/ServiceQuestionnairePage';
import SignPage from './components/pages/SignPage';
import InfoRequestPage from './components/pages/InfoRequestPage';
import ConsultationsRedirect from './components/pages/ConsultationsRedirect';
import Header from './Layout/header';
import Sidebar from './Layout/sidebar';
import { initiateSocketConnection, disconnectSocket } from './socket';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from "jwt-decode";
import './App.css';
import { ToastProvider } from './Context/ToastContext';
import HomeIcon from './Images/home-icon.svg';
import ConversationsIcon from './Images/conversations-icon.svg';
import LogoutIcon from './Images/logout-icon.svg';

const skipSocket = new URLSearchParams(window.location.search).get('skipSocket') === '1';

// Key to store last SetMetting URL in localStorage
const LAST_SETMETTING_URL = 'last_setmetting_url';
// Same idea for other deep links that must survive the Auth0 round-trip (set by ProtectedRoute).
// sessionStorage, and removed right after use: it can carry ?email= and must not outlive the tab.
const RETURN_TO_AFTER_LOGIN = 'return_to_after_login';

const MainContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  height: 95%;
  width: 100%;
  overflow: hidden;
  
  @media (max-width: 768px) {
    flex-direction: column;
    height: 100vh;
    min-height: 0;
    overflow: hidden;
    padding-top: 50px;
    padding-bottom: 65px;
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 100%;
    order: 2;
  }
`;

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 100vh;
    min-height: 100vh;
    max-height: 100vh;
    overflow: hidden;
    padding-top: 0;
  }
`;

// New component for mobile bottom navigation
const MobileBottomNav = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 50px;
    background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
    z-index: 1000;
    justify-content: space-around;
    align-items: center;
    border-top: 1px solid #e0e8f5;
    box-shadow: 0px -2px 10px rgba(39, 68, 131, 0.07);
  }
`;

const NavButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.$active ? '#65748d' : '#33445e'};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 33.33%;
  height: 100%;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
  
  &:hover {
    color: ${props => props.$active ? '#65748d' : '#192947'};
  }
  
  &:active {
    background-color: rgba(25, 41, 71, 0.03);
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 30px;
    height: 3px;
    background-color: #cad5e9;
    border-radius: 1.5px;
    opacity: ${props => props.$active ? 1 : 0};
    transition: opacity 0.3s ease;
  }

  img {
    width: 24px;
    height: 24px;
    filter: ${props => props.$active ? 'brightness(0) saturate(100%) invert(9%) sepia(17%) saturate(6773%) hue-rotate(204deg) brightness(91%) contrast(87%)' : 'brightness(0) saturate(100%) invert(34%) sepia(15%) saturate(1133%) hue-rotate(179deg) brightness(88%) contrast(88%)'};
  }
`;

const App = () => {
  const [expanded, setExpanded] = useState(true);
  const { isAuthenticated, getAccessTokenSilently, user, isLoading } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const [hasHandledAuth, setHasHandledAuth] = useState(false);

  // Handle Auth0 callback and navigation logic
  useEffect(() => {
    // Detect Auth0 callback by checking for code and state in the query string
    //const isAuthCallback = window.location.search.includes('code=') && window.location.search.includes('state=');

    if (isAuthenticated && !isLoading && !hasHandledAuth) {
      // Prevent multiple navigations
      setHasHandledAuth(true);

      // Retrieve the intended URL from Auth0's appState (if available) or from localStorage
      let pendingReturn = null;
      try { pendingReturn = sessionStorage.getItem(RETURN_TO_AFTER_LOGIN); sessionStorage.removeItem(RETURN_TO_AFTER_LOGIN); } catch { /* storage blocked */ }
      const returnToUrl = (location.state && location.state.returnTo) || localStorage.getItem(LAST_SETMETTING_URL) || pendingReturn;
      console.log("Auth complete. Intended URL:", returnToUrl);

      // Clean URL parameters by replacing the current URL with the intended URL (or fallback to '/')
      window.history.replaceState({}, document.title, returnToUrl || '/');

      if (returnToUrl) {
        setTimeout(() => {
          console.log("Navigating to:", returnToUrl);
          localStorage.removeItem(LAST_SETMETTING_URL)
          navigate(returnToUrl, { replace: true });
        }, 100);
      }
    }

    // Reset the navigation flag when not handling an Auth0 callback
    if (!window.location.search.includes('code=')) {
      setHasHandledAuth(false);
    }
  }, [isAuthenticated, isLoading, location, navigate, hasHandledAuth]);

  const toggleFlag = () => {
    setExpanded((expanded) => !expanded);
  };

  useEffect(() => {
    // Connect to the socket server when authenticated
    const connectSocket = async () => {
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          const decodedToken = jwtDecode(token);
          const userData = {
            userId: user.sub,
            username: user.name,
            email: user.email,
            preferred_language: decodedToken.preferred_language,
            token: token
          };
          initiateSocketConnection(userData);
        } catch (error) {
          console.error('Error getting access token:', error);
        }
      }
    };

    // Only connect socket if not skipping socket connection
    if (!skipSocket) {
      connectSocket();
    }

    // Disconnect socket when unmounting
    return () => {
      if (isAuthenticated && !skipSocket) {
        disconnectSocket();
      }
    };
  }, [isAuthenticated, getAccessTokenSilently, user, skipSocket]);

  // Check if current route is a standalone page (no header/sidebar needed)
  const isStandalonePage = location.pathname.startsWith('/service-questionnaire/')
    || location.pathname.startsWith('/sign/')
    || location.pathname.startsWith('/info/');

  // Render standalone pages without the main layout
  if (isStandalonePage) {
    return (
      <ToastProvider>
        <Routes>
          <Route path="/service-questionnaire/:sessionId" element={<ServiceQuestionnairePage />} />
          {/* Public agreement signing — no login, opened from the emailed link */}
          <Route path="/sign/:requestCode" element={<SignPage />} />
          {/* Public "ask the client" follow-up — no login, opened from the emailed link */}
          <Route path="/info/:requestCode" element={<InfoRequestPage />} />
        </Routes>
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <AppContainer>
        <Header onExpandToggle={toggleFlag} expanded={expanded} />
        <MainContainer>
          <Sidebar expanded={expanded} onExpandToggle={toggleFlag} />
          <ContentContainer>
            <Routes>
              <Route path="/" element={<ProtectedRoute><ExpertBoard /></ProtectedRoute>} />
              <Route path="/canvas" element={<Canvas />} />
              <Route path="/conversations" element={<ProtectedRoute><ConversationPage /></ProtectedRoute>} />
              <Route path="/conversations/:threadId" element={<ProtectedRoute><ConversationPage /></ProtectedRoute>} />
              {/* Marketplace (imavenai.com) consultations live on Home; old links redirect there with their query */}
              <Route path="/consultations" element={<ProtectedRoute><ConsultationsRedirect /></ProtectedRoute>} />
              <Route path="/setMetting/:expertId/:threadId" element={<ProtectedRoute><SetMetting /></ProtectedRoute>} />
              <Route path="/ai-analysis/:threadId" element={<ProtectedRoute><AiAnalysisView /></ProtectedRoute>} />
              <Route path="/video-call/:expertId/:channelName" element={<ProtectedRoute><VideoCallPage /></ProtectedRoute>} />
              <Route path="/whiteboard/:roomId" element={<ProtectedRoute><WhiteboardPage /></ProtectedRoute>} />
              <Route path="/contact-us" element={<ProtectedRoute><ContactUs /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              
              {/* Form Filling - Protected by email verification */}
              <Route path="/fill-form/:sessionId" element={
                <FormFillingProtectedRoute>
                  <FormFillingPage />
                </FormFillingProtectedRoute>
              } />
              
              {/* Payment Page - Public access via short code */}
              <Route path="/pay/:shortCode" element={<PaymentPage />} />

            </Routes>
          </ContentContainer>
        </MainContainer>
      </AppContainer>
    </ToastProvider>
  );
};

export default App;
